import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getOrganizationUsers,
  getPendingInvites,
  createInvite,
  revokeInvite,
  resendInvite,
  removeUser,
  type CreateInviteRequest,
} from '../api/users';

export function useOrganizationUsers() {
  return useQuery({
    queryKey: ['organizationUsers'],
    queryFn: getOrganizationUsers,
    staleTime: 2 * 60 * 1000,
  });
}

export function usePendingInvites() {
  return useQuery({
    queryKey: ['pendingInvites'],
    queryFn: getPendingInvites,
    staleTime: 1 * 60 * 1000,
  });
}

export function useCreateInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateInviteRequest) => createInvite(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingInvites'] });
    },
  });
}

export function useRevokeInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inviteId: string) => revokeInvite(inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingInvites'] });
    },
  });
}

export function useResendInvite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inviteId: string) => resendInvite(inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingInvites'] });
    },
  });
}

export function useRemoveUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason?: string }) =>
      removeUser(userId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organizationUsers'] });
    },
  });
}
